import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, unref as _unref, vShow as _vShow, withDirectives as _withDirectives, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "bar-chart" }
const _hoisted_2 = { class: "bar-chart__card" }
const _hoisted_3 = { class: "bar-chart__card-header" }
const _hoisted_4 = {
  key: 0,
  class: "bar-chart__average"
}
const _hoisted_5 = {
  key: 0,
  class: "bar-chart__average-shimmer"
}
const _hoisted_6 = {
  key: 1,
  class: "bar-chart__average-value"
}
const _hoisted_7 = {
  class: "bar-chart__view-by",
  role: "group",
  "aria-label": "View by period"
}
const _hoisted_8 = ["onClick"]
const _hoisted_9 = {
  key: 0,
  class: "bar-chart__view-sep",
  "aria-hidden": "true"
}
const _hoisted_10 = {
  key: 0,
  class: "bar-chart__skeleton bar-chart__skeleton--phase1"
}

import {
    ref,
    computed,
    watch,
    onBeforeUnmount,
} from 'vue';
import Chart from 'primevue/chart';
import { todayIn, startOf, calendarToJSDate } from '@/utils/dt';
import { useTimezoneStore } from '@/stores/timezone';
import { formatCurrency, formatCompactCurrency } from '@/utils/formatUtils';

type ViewMode = 'days' | 'weeks' | 'months'

interface Props {
    dailyAverage?: string | null
    chartData: Record<string, any>
    chartOptions?: Record<string, any>
    loading?: boolean
    /**
     * Paint the bar matching today at full opacity. Which bar that is comes from the range
     * `normalizePaymentsChartRange` returned, not from here — `currentPeriodBarIndex` below only
     * matches a label against today and highlights whatever it finds. If the highlight looks
     * like it is on the wrong bar, the window is the thing to look at, not this component.
     */
    highlightCurrentPeriod?: boolean
    /** Emit barClick and show a pointer cursor. Off for read-only charts (Reports Accounts). */
    clickable?: boolean
    valueFormatter?: (val: number) => string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BarChart',
  props: {
    dailyAverage: { default: null },
    chartData: {},
    chartOptions: { default: () => ({}) },
    loading: { type: Boolean, default: false },
    highlightCurrentPeriod: { type: Boolean, default: false },
    clickable: { type: Boolean, default: false },
    valueFormatter: { type: Function, default: undefined }
  },
  emits: ["update:viewMode", "barClick"],
  setup(__props: any, { emit: __emit }) {

/* eslint-env vue/setup-compiler-macros */
/* eslint-disable @typescript-eslint/no-explicit-any */
const VIEW_MODES: ViewMode[] = ['days', 'weeks', 'months'];

const props = __props;

const emit = __emit;

const resolvedFormatter = computed(() => props.valueFormatter ?? formatCurrency);
const isGrouped = computed(() => (props.chartData?.datasets?.length ?? 0) > 1);

const viewMode = ref<ViewMode>('days');
const hoveredBarIndex = ref<number | null>(null);
const chartRef = ref<InstanceType<typeof Chart> | null>(null);
const shouldRenderChart = ref(false);
const tzStore = useTimezoneStore();

watch(() => props.loading, (loading) => {
    if (!loading) shouldRenderChart.value = true;
}, { immediate: true });

// ── Current period detection ──────────────────────────────────────────────
const toMonthDay = (d: Date) => (
    `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`
);

const normSlashLabel = (label: string) => {
    const m = label.match(/(\d{1,2})\D+(\d{1,2})/);
    return m ? `${String(+m[1]).padStart(2, '0')}/${String(+m[2]).padStart(2, '0')}` : null;
};

const normMonthLabel = (s: string) => s.trim().toLowerCase().replace('.', '').slice(0, 3);

const currentPeriodBarIndex = computed<number | null>(() => {
    if (!props.highlightCurrentPeriod) return null;
    const raw = props.chartData?.labels;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    const labels = raw.map(String);
    const tz = tzStore.effectiveTimezone;
    if (viewMode.value === 'days') {
        const today = toMonthDay(calendarToJSDate(todayIn(tz)));
        const i = labels.findIndex((l) => normSlashLabel(l) === today);
        return i >= 0 ? i : null;
    }
    if (viewMode.value === 'weeks') {
        const weekStart = toMonthDay(calendarToJSDate(startOf('week', tz)));
        const i = labels.findIndex((l) => normSlashLabel(l) === weekStart);
        return i >= 0 ? i : null;
    }
    // months: compare 3-letter month abbreviation
    const todayCal = todayIn(tz);
    const [, monthStr] = todayCal.split('-');
    const monthDate = calendarToJSDate(`${todayCal.split('-')[0]}-${monthStr}-01`);
    const thisMonth = normMonthLabel(monthDate.toLocaleString('en-US', { month: 'short' }));
    const i = labels.findIndex((l) => normMonthLabel(l) === thisMonth);
    return i >= 0 ? i : null;
});

const setViewMode = (mode: ViewMode) => {
    viewMode.value = mode;
    emit('update:viewMode', mode);
};

// ── RAF-driven gradient animation ──────────────────────────────────────────
let rafId: number | null = null;
let gradientAlpha = 0;
let gradientBar: number | null = null;
let currentBar: number | null = null;

const startGradientAnim = (targetBarIdx: number | null) => {
    if (targetBarIdx !== null) gradientBar = targetBarIdx;
    const targetAlpha = targetBarIdx !== null ? 1 : 0;
    const fromAlpha = gradientAlpha;
    const startTime = performance.now();
    const DURATION = 200;

    if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }

    const step = (now: number) => {
        const t = Math.min((now - startTime) / DURATION, 1);
        const eased = 1 - (1 - t) ** 2; // easeOutQuad
        gradientAlpha = fromAlpha + (targetAlpha - fromAlpha) * eased;

        const inst = (chartRef.value as any)?.chart;
        if (inst) inst.update('none');

        if (t < 1) {
            rafId = requestAnimationFrame(step);
        } else {
            gradientAlpha = targetAlpha;
            if (targetAlpha === 0) gradientBar = null;
            rafId = null;
        }
    };

    rafId = requestAnimationFrame(step);
};

watch(hoveredBarIndex, (newIdx) => { startGradientAnim(newIdx); });

watch(currentPeriodBarIndex, (idx) => {
    currentBar = idx;
    const inst = (chartRef.value as any)?.chart;
    if (inst) inst.update('none');
}, { immediate: true });

onBeforeUnmount(() => { if (rafId !== null) cancelAnimationFrame(rafId); });

// ── Mouse handlers ──────────────────────────────────────────────────────────
const resolveIndex = (
    event: MouseEvent,
): { index: number | null; inPlot: boolean; inBar: boolean; datasetIndex: number | null } => {
    const inst = (chartRef.value as any)?.chart;
    if (!inst) return { index: null, inPlot: false, inBar: false, datasetIndex: null };

    const rect = inst.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const area = inst.chartArea;

    if (y >= area.top && y <= area.bottom) {
        const elements = inst.getElementsAtEventForMode(event, 'index', { intersect: false }, true);
        const hit = elements[0];

        const barAt = elements.findIndex((el: any) => el?.element?.inRange?.(x, y, true) === true);
        return {
            index: hit?.index ?? null,
            inPlot: true,
            inBar: barAt >= 0,
            datasetIndex: barAt >= 0 ? elements[barAt].datasetIndex : null,
        };
    }

    const scale = inst.scales?.x;
    if (!scale || y < scale.top || y > scale.bottom || x < scale.left || x > scale.right) {
        return { index: null, inPlot: false, inBar: false, datasetIndex: null };
    }
    const index = Math.round(scale.getValueForPixel(x));
    const count = inst.data?.labels?.length ?? 0;
    return {
        index: index >= 0 && index < count ? index : null,
        inPlot: false,
        inBar: false,
        datasetIndex: null,
    };
};


const canNavigate = (index: number | null, inBar: boolean) => (
    index !== null && (!isGrouped.value || inBar)
);

const onChartMouseMove = (event: MouseEvent) => {
    const { index, inPlot, inBar } = resolveIndex(event);

    if (props.clickable) {
        const host = event.currentTarget as HTMLElement | null;
        if (host) host.style.cursor = canNavigate(index, inBar) ? 'pointer' : 'default';
    }

    const nextIndex = inPlot && inBar ? index : null;
    if (hoveredBarIndex.value !== nextIndex) hoveredBarIndex.value = nextIndex;
};

const onChartClick = (event: MouseEvent) => {
    if (!props.clickable) return;
    const { index, inBar, datasetIndex } = resolveIndex(event);
    if (canNavigate(index, inBar)) emit('barClick', index as number, datasetIndex);
};

const onChartMouseLeave = () => {
    if (hoveredBarIndex.value !== null) hoveredBarIndex.value = null;
};

// ── Chart plugin ────────────────────────────────────────────────────────────
const chartPlugins = [{
    id: 'gradientOverlay',
    afterDatasetsDraw(chart: any) {
        const { ctx } = chart as { ctx: CanvasRenderingContext2D };
        const datasetCount = (chart.data.datasets?.length ?? 0) as number;
        if (datasetCount === 0) return;
        const isMulti = datasetCount > 1;

        const drawGradientBar = (dsIdx: number, barIdx: number, alpha: number) => {
            const meta = chart.getDatasetMeta(dsIdx);
            const dataset = chart.data.datasets?.[dsIdx];
            const bar = meta.data[barIdx];
            if (!bar) return;
            const {
                x: bx, y, base, width: bw,
            } = (bar as any).getProps(['x', 'y', 'base', 'width'], true);
            const x = bx - bw / 2;
            const w = bw;
            const h = base - y;
            if (h <= 0 || w <= 0) return;

            // ── Gradient fill ──────────────────────────────────────────
            const grad = ctx.createLinearGradient(0, y, 0, base);
            grad.addColorStop(0, '#A61B78');
            grad.addColorStop(1, '#FC7C18');

            const r = Math.min(12, w / 2, h / 2);
            ctx.save();
            ctx.globalAlpha = alpha;
            ctx.beginPath();
            ctx.moveTo(x + r, y);
            ctx.lineTo(x + w - r, y);
            ctx.arcTo(x + w, y, x + w, y + r, r);
            ctx.lineTo(x + w, y + h);
            ctx.lineTo(x, y + h);
            ctx.arcTo(x, y, x + r, y, r);
            ctx.closePath();
            ctx.fillStyle = grad;
            ctx.fill();
            ctx.restore();

            // ── Vertical label — single-dataset only ──────────────────
            if (isMulti) return;

            const rawVal = dataset?.data?.[barIdx];
            if (typeof rawVal !== 'number') return;

            const formatter = resolvedFormatter.value;
            const fullLabel = formatter(rawVal);
            const topPadding = Math.max(4, Math.min(16, Math.floor(h * 0.14)));
            const sidePadding = Math.max(4, Math.min(10, Math.floor(w * 0.2)));
            const maxLabelH = Math.max(0, h - topPadding * 2);
            const maxFontSize = Math.max(6, Math.floor(w - sidePadding));
            if (maxLabelH <= 0 || maxFontSize <= 0) return;

            const noDecimals = fullLabel.replace(/\.\d{1,2}$/, '');
            const compact = formatCompactCurrency(rawVal);
            const candidates = [fullLabel];
            if (!candidates.includes(noDecimals)) candidates.push(noDecimals);
            if (!candidates.includes(compact)) candidates.push(compact);

            let fittedLabel = '';
            let fittedFontSize = 0;
            candidates.some((cand) => {
                let fs = Math.min(15, maxFontSize);
                while (fs >= 6) {
                    ctx.font = `600 ${fs}px Archivo`;
                    if (ctx.measureText(cand).width <= maxLabelH) {
                        fittedLabel = cand;
                        fittedFontSize = fs;
                        return true;
                    }
                    fs -= 1;
                }
                return false;
            });

            if (!fittedLabel || fittedFontSize <= 0) return;

            ctx.save();
            ctx.globalAlpha = alpha;
            ctx.translate(bx, y + topPadding);
            ctx.rotate(-Math.PI / 2);
            ctx.fillStyle = '#FFFFFF';
            ctx.font = `600 ${fittedFontSize}px Archivo`;
            ctx.textAlign = 'right';
            ctx.textBaseline = 'middle';
            ctx.fillText(fittedLabel, 0, 0);
            ctx.restore();
        };

        const drawAllAt = (barIdx: number, alpha: number) => {
            for (let d = 0; d < datasetCount; d++) {
                drawGradientBar(d, barIdx, alpha);
            }
        };

        // Always draw the current period bar at full opacity
        if (currentBar !== null) drawAllAt(currentBar, 1);

        // Draw hovered bar on top (animated) — skip if same bar as current period
        if (gradientBar !== null && gradientAlpha > 0 && gradientBar !== currentBar) {
            drawAllAt(gradientBar, gradientAlpha);
        }
    },
}];

const maxDataValue = computed(() => {
    const datasets: Record<string, any>[] = props.chartData.datasets || [];
    const values: number[] = datasets.flatMap((ds) => (ds.data as number[]) || []);
    return Math.max(0, ...values);
});

const resolvedChartData = computed(() => props.chartData);

const resolvedOptions = computed(() => {
    const max = maxDataValue.value;

    const raw = max / 5;
    const magnitude = 10 ** Math.floor(Math.log10(raw || 1));
    const stepSize = Math.max(1, Math.ceil(raw / magnitude) * magnitude);

    return {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
        },
        scales: {
            x: {
                grid: { display: false },
                border: { display: false },
                ticks: {
                    color: 'rgba(0,0,0,0.5)',
                    font: { family: 'Archivo', size: 13 },
                },
            },
            y: {
                grid: { color: 'rgba(0,0,0,0.09)' },
                border: { display: false },
                ticks: {
                    color: 'rgba(0,0,0,0.5)',
                    font: { family: 'Archivo', size: 13 },
                    stepSize,
                    callback: (val: number) => {
                        if (val === 0) return '0';
                        if (val >= 1000000) return `${val / 1000000}M`;
                        if (val >= 1000) return `${val / 1000}k`;
                        return String(val);
                    },
                },
            },
        },
        ...props.chartOptions,
    };
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        (__props.dailyAverage !== null && (__props.loading || __props.dailyAverage))
          ? (_openBlock(), _createElementBlock("p", _hoisted_4, [
              _cache[0] || (_cache[0] = _createElementVNode("span", { class: "bar-chart__average-label" }, "Your Daily Average", -1)),
              (__props.loading)
                ? (_openBlock(), _createElementBlock("span", _hoisted_5))
                : (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(__props.dailyAverage), 1))
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_7, [
          _cache[1] || (_cache[1] = _createElementVNode("span", { class: "bar-chart__view-label" }, "View by:", -1)),
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(VIEW_MODES, (mode, i) => {
            return (_openBlock(), _createElementBlock(_Fragment, { key: mode }, [
              _createElementVNode("button", {
                class: _normalizeClass(["bar-chart__view-btn", { 'bar-chart__view-btn--active': viewMode.value === mode }]),
                type: "button",
                onClick: ($event: any) => (setViewMode(mode))
              }, _toDisplayString(mode.charAt(0).toUpperCase() + mode.slice(1)), 11, _hoisted_8),
              (i < VIEW_MODES.length - 1)
                ? (_openBlock(), _createElementBlock("span", _hoisted_9, "/"))
                : _createCommentVNode("", true)
            ], 64))
          }), 64))
        ])
      ]),
      _createElementVNode("div", {
        class: "bar-chart__chart-wrap",
        onClick: onChartClick,
        onMousemove: onChartMouseMove,
        onMouseleave: onChartMouseLeave,
        onBlur: onChartMouseLeave
      }, [
        (__props.loading)
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(20, (n) => {
                return _createElementVNode("div", {
                  key: n,
                  class: "bar-chart__skeleton-bar bar-chart__skeleton-bar--phase1",
                  style: _normalizeStyle({ height: [45, 25, 55, 70, 65, 80, 60, 40, 72, 50][(n - 1) % 10] + '%' })
                }, null, 4)
              }), 64))
            ]))
          : _createCommentVNode("", true),
        (shouldRenderChart.value)
          ? _withDirectives((_openBlock(), _createBlock(_unref(Chart), {
              key: 1,
              ref_key: "chartRef",
              ref: chartRef,
              type: "bar",
              data: resolvedChartData.value,
              options: resolvedOptions.value,
              plugins: chartPlugins
            }, null, 8, ["data", "options"])), [
              [_vShow, !__props.loading]
            ])
          : _createCommentVNode("", true)
      ], 32)
    ])
  ]))
}
}

})